import React, { useState, useEffect, useRef } from 'react';
import { employeeApi } from '../services/hrApi';
import type { DashboardStats, RecentActivity } from '../types';
import './PageStyles.scss';
import './Dashboard.scss';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const statsGridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await employeeApi.getDashboardStats();
      setStats(data);
    } catch (err: any) {
      console.error('Error fetching dashboard stats:', err);
      setError(err?.message || 'Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const scrollMetrics = (direction: 'left' | 'right') => {
    if (statsGridRef.current) {
      const scrollAmount = 300;
      const scrollTo = direction === 'left' 
        ? statsGridRef.current.scrollLeft - scrollAmount
        : statsGridRef.current.scrollLeft + scrollAmount;
      
      statsGridRef.current.scrollTo({
        left: scrollTo,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="hr-portal-page">
      <div className="hr-portal-page-header">
        <h2>Dashboard</h2>
        <p className="page-description">Overview of HR metrics and key information</p>
      </div>
      <div className="hr-portal-page-content">
        {loading && (
          <div className="dashboard-loading">
            <p>Loading dashboard statistics...</p>
          </div>
        )}

        {error && (
          <div className="dashboard-error">
            <p>{error}</p>
            <button onClick={fetchDashboardStats} className="btn-retry">
              Retry
            </button>
          </div>
        )}

        {!loading && !error && stats && (
          <>
            <div className="dashboard-stats-container">
              <button 
                className="dashboard-scroll-btn scroll-left"
                onClick={() => scrollMetrics('left')}
                aria-label="Scroll left"
              >
                <i className="bx bx-chevron-left"></i>
              </button>
              <div className="dashboard-stats-grid" ref={statsGridRef}>
              <div className="dashboard-stat-card stat-total">
                <div className="stat-icon">
                  <i className="bx bx-group"></i>
                </div>
                <div className="stat-content">
                  <h3 className="stat-label">Total Employees</h3>
                  <p className="stat-value">{stats.metrics.total_employees}</p>
                </div>
              </div>

              <div className="dashboard-stat-card stat-active">
                <div className="stat-icon">
                  <i className="bx bx-user-check"></i>
                </div>
                <div className="stat-content">
                  <h3 className="stat-label">Active Employees</h3>
                  <p className="stat-value">{stats.metrics.active}</p>
                </div>
              </div>

              <div className="dashboard-stat-card stat-onboarding">
                <div className="stat-icon">
                  <i className="bx bx-clipboard"></i>
                </div>
                <div className="stat-content">
                  <h3 className="stat-label">Onboarding</h3>
                  <p className="stat-value">{stats.metrics.onboarding}</p>
                </div>
              </div>

              <div className="dashboard-stat-card stat-inactive">
                <div className="stat-icon">
                  <i className="bx bx-user-x"></i>
                </div>
                <div className="stat-content">
                  <h3 className="stat-label">Inactive</h3>
                  <p className="stat-value">{stats.metrics.inactive}</p>
                </div>
              </div>

              <div className="dashboard-stat-card stat-documents">
                <div className="stat-icon">
                  <i className="bx bx-file"></i>
                </div>
                <div className="stat-content">
                  <h3 className="stat-label">Pending Documents</h3>
                  <p className="stat-value">{stats.metrics.pending_documents}</p>
                </div>
              </div>

              <div className="dashboard-stat-card stat-attendance">
                <div className="stat-icon">
                  <i className="bx bx-calendar-check"></i>
                </div>
                <div className="stat-content">
                  <h3 className="stat-label">Present Today</h3>
                  <p className="stat-value">{stats.metrics.present_today}</p>
                </div>
              </div>

              <div className="dashboard-stat-card stat-hires">
                <div className="stat-icon">
                  <i className="bx bx-user-plus"></i>
                </div>
                <div className="stat-content">
                  <h3 className="stat-label">Recent Hires (30d)</h3>
                  <p className="stat-value">{stats.metrics.recent_hires}</p>
                </div>
              </div>

              <div className="dashboard-stat-card stat-onboarding-steps">
                <div className="stat-icon">
                  <i className="bx bx-task"></i>
                </div>
                <div className="stat-content">
                  <h3 className="stat-label">Pending Onboarding Steps</h3>
                  <p className="stat-value">{stats.metrics.pending_onboarding_steps}</p>
                </div>
              </div>
              </div>
              <button 
                className="dashboard-scroll-btn scroll-right"
                onClick={() => scrollMetrics('right')}
                aria-label="Scroll right"
              >
                <i className="bx bx-chevron-right"></i>
              </button>
            </div>

            {stats.recent_activities && stats.recent_activities.length > 0 && (
              <div className="dashboard-recent-activity">
                <h3 className="activity-section-title">Recent Activity</h3>
                <div className="activity-list">
                  {stats.recent_activities.map((activity: RecentActivity, index: number) => (
                    <div key={index} className="activity-item">
                      <div className="activity-icon">
                        {activity.type === 'new_employee' && <i className="bx bx-user-plus"></i>}
                        {activity.type === 'employee_update' && <i className="bx bx-edit"></i>}
                        {activity.type === 'document_update' && <i className="bx bx-file"></i>}
                        {activity.type === 'attendance' && <i className="bx bx-calendar-check"></i>}
                      </div>
                      <div className="activity-content">
                        <h4 className="activity-title">{activity.title}</h4>
                        <p className="activity-description">{activity.description}</p>
                        <span className="activity-time">
                          {new Date(activity.timestamp).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
